import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Experiences } from '@local/client-contracts';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AssistantFilterPriority } from '../../enum/assistant-filter-priority.enum';
import { FilterSourceViewModel } from '../../models/filter-source-view-model';
import { filtersSourceActions } from '../../helpers/assistant.content';
import { AppPopupComponent, AppPopupData } from 'src/app/bar/components/app-popup/app-popup.component';
import { PopupRef, PopupService } from '@local/ui-infra';
import { take } from 'rxjs';
import { WARNING_POPUP_BASE_DATA, WARNING_POPUP_CONTENT, warningPopupAction, warningPopupReason } from './warning-actions-data';

@UntilDestroy()
@Component({
  selector: 'assistant-filter-buttons',
  templateUrl: './assistant-filter-buttons.component.html',
  styleUrls: ['./assistant-filter-buttons.component.scss'],
})
export class AssistantFilterButtonsComponent {
  readonly actionsContent = filtersSourceActions;
  readonly assistantFilterPriority = AssistantFilterPriority;

  @Input() index: number;
  @Input() permissionRole: Experiences.PermissionRole;
  @Input() filtersDataSourcesCount: number;
  @Input() filterSourceView: FilterSourceViewModel;
  @Input() disableTiering: boolean;

  @Output() addPriority = new EventEmitter<number>();
  @Output() onRemoveLine = new EventEmitter<number>();

  private warningPopupRef: PopupRef<AppPopupComponent, AppPopupData>;

  get viewOnly() {
    return this.permissionRole === 'viewer';
  }

  get filterSource() {
    return this.filterSourceView.filterSource;
  }

  constructor(private popupService: PopupService) {}

  get priorityTooltip() {
    if (this.filterSource.tier === this.assistantFilterPriority.HIGH) {
      if (this.viewOnly) {
        return this.actionsContent.priorityView;
      }
      return this.actionsContent.priorityOn;
    }
    if (['deleted', 'creatorNoAccess'].includes(this.filterSourceView?.unavailableState)) {
      return '';
    }
    if (this.filtersDataSourcesCount <= 1) {
      return this.actionsContent.priorityOffDisabled;
    }
    if (!this.filterSource?.filters?.app) {
      return '';
    }
    return this.actionsContent.priorityOff;
  }

  removeLine() {
    if (this.filterSourceView?.unavailableState === 'noAccess') {
      return this.openWarningPopup('remove', 'noAccess', () => this.onRemoveLine.emit(this.index));
    }
    this.onRemoveLine.emit(this.index);
  }

  priorityChange() {
    if (this.filterSourceView?.unavailableState === 'noAccess') {
      return this.openWarningPopup('priority', 'noAccess', () => this.addPriority.emit(this.index));
    }
    this.addPriority.emit(this.index);
  }

  private openWarningPopup(action: warningPopupAction, reason: warningPopupReason, actionFunc: () => void) {
    if (this.warningPopupRef) {
      this.closeWarningPopup();
    }
    const data: AppPopupData = WARNING_POPUP_BASE_DATA;
    const content = WARNING_POPUP_CONTENT[action];
    data.content.title = content.title;
    data.message = content.message[reason];
    this.warningPopupRef = this.popupService.open<AppPopupComponent, AppPopupData>('center', AppPopupComponent, data, {
      position: 'center',
      closeOnClickOut: true,
      backdropStyle: 'blur-2',
    });
    this.warningPopupRef.compInstance.primaryButton.pipe(take(1), untilDestroyed(this)).subscribe(() => {
      actionFunc();
      this.closeWarningPopup();
    });
    this.warningPopupRef.compInstance.secondaryButton.pipe(take(1), untilDestroyed(this)).subscribe(() => {
      this.closeWarningPopup();
    });
  }

  private closeWarningPopup() {
    this.warningPopupRef.destroy();
    this.warningPopupRef = null;
  }
}
